import { config } from '../utils/config.js';
import deviceManager from './DeviceManager.js';

export class RestaurantAPI {
  constructor() {
    this.baseURL = config.getApiUrl();
    this.isDevMode = config.isDev();
  }

  /**
   * Get current auth token (may be updated by device injection)
   */
  get authToken() {
    return config.getAuthToken();
  }

  /**
   * Check if running on Android device (dynamic check)
   */
  get isAndroidDevice() {
    return deviceManager.isAndroid();
  }

  /**
   * Fetch restaurant configuration from API
   * @param {string} restaurantId - Restaurant ID to fetch configuration for
   * @returns {Promise<Object>} Restaurant configuration
   */
  async getRestaurantConfig(restaurantId) {
    try {
      let url, headers;

      if (this.isAndroidDevice) {
        url = `${this.baseURL}/api/v1/device/restaurants/configuration?advanced=false`;
        headers = {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
      } else {
        url = `${this.baseURL}/api/v1/restaurants/${restaurantId}/configuration?advanced=false`;
        headers = {
          'Authorization': `Bearer ${this.authToken}`,
          'X-Restaurant-Id': restaurantId,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const config = await response.json();
      return this.transformAPIResponse(config, restaurantId);
    } catch (error) {
      console.error('Failed to fetch restaurant config from API:', error);
      throw error;
    }
  }

  /**
   * Fetch menu data (categories and items) from API
   * @param {string} restaurantId - Restaurant ID to fetch menu for
   * @returns {Promise<Object>} Menu data with categories and items
   */
  async getMenuData(restaurantId) {
    try {
      let url, headers;

      if (this.isAndroidDevice) {
        url = new URL(`${this.baseURL}/api/v1/device/menus/active`);
        url.searchParams.set('includeCategories', 'true');
        url.searchParams.set('includeItems', 'true');
        url.searchParams.set('includeModifiers', 'true');

        headers = {
          'Authorization': `Bearer ${this.authToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
      } else {
        url = new URL(`${this.baseURL}/api/v1/menus/active`);
        url.searchParams.set('includeCategories', 'true');
        url.searchParams.set('includeItems', 'true');
        url.searchParams.set('includeModifiers', 'true');
        url.searchParams.set('restaurantId', restaurantId);

        headers = {
          'Authorization': `Bearer ${this.authToken}`,
          'X-Restaurant-Id': restaurantId,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        if (response.status === 404) {
          return this.transformMenuResponse({ categories: [], items: [] });
        }
        throw new Error(`Menu API request failed: ${response.status} ${response.statusText}`);
      }

      const menuData = await response.json();

      return this.transformMenuResponse(menuData);
    } catch (error) {
      console.error('Failed to fetch menu data from API:', error);
      throw error;
    }
  }

  /**
   * Transform API response to our internal config structure
   * @param {Object} apiData - Raw API response
   * @param {string} restaurantId - Restaurant ID
   * @returns {Object} Transformed configuration
   */
  transformAPIResponse(apiData, restaurantId) {
    const paymentFlowConfiguration = apiData.paymentFlowConfiguration || (
      (apiData.paymentTiming || apiData.topbarNotification || apiData.paymentMethods || apiData.staffIntegration)
        ? {
            paymentTiming: apiData.paymentTiming,
            sessionTimeoutMinutes: apiData.sessionTimeoutMinutes,
            welcomeScreen: apiData.welcomeScreen,
            topbarNotification: apiData.topbarNotification,
            paymentMethods: apiData.paymentMethods,
            staffIntegration: apiData.staffIntegration,
          }
        : null
    )

    // Note: Menu data will be fetched separately from menu endpoint
    return {
      id: apiData.restaurantId || restaurantId,
      name: apiData.restaurantName || 'Restaurant',
      tagline: apiData.kioskDisplaySettings?.welcomeScreen?.welcomeText || 'Welcome to our restaurant - enjoy our carefully crafted menu.',
      subtext: apiData.kioskDisplaySettings?.welcomeScreen?.subText || 'Please tap to start ordering',
      branding: {
        primaryColor: this.extractColor(apiData, 'primary') || 'rgb(62, 136, 91)',
        secondaryColor: this.extractColor(apiData, 'secondary') || 'rgb(249, 200, 70)',
        accent: this.extractColor(apiData, 'accent') || 'rgb(199, 217, 65)',
        logo: this.extractLogo(apiData) || null,
        logoSettings: apiData?.kioskDisplaySettings?.welcomeScreen?.logoSettings || null,
        backgroundImage: this.extractBackgroundImage(apiData) || null,
        backgroundImageOpacity: apiData?.kioskDisplaySettings?.welcomeScreen?.backgroundImageOpacity || 100
      },
      menu: {
        categories: [],
        items: {},
        specials: []
      },
      contact: {
        phone: null,
        email: null,
        address: null
      },
      settings: {
        currency: apiData.orderingConfiguration?.defaultCurrency || 'PLN',
        language: apiData.kioskDisplaySettings?.defaultLanguage || 'en',
        timezone: null
      },
      kioskDisplaySettings: apiData.kioskDisplaySettings || {},
      paymentFlowConfiguration,
      uiPreferences: apiData.uiPreferences || {},
      orderingConfiguration: apiData.orderingConfiguration || {}
    };
  }

  /**
   * Transform menu API response to our internal menu structure
   * @param {Object} menuData - Raw menu API response
   * @returns {Object} Transformed menu data
   */
  transformMenuResponse(menuData) {
    const rawCategories = menuData?.categories || [];

    const categories = [];
    const items = {};
    const specials = [];

    const pushSpecial = (item, category) => {
      specials.push({
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: item.price,
        currency: item.currency || (menuData?.currency ?? 'PLN'),
        currencySymbol: item.currencySymbol || null,
        image: item.image || null,
        categoryId: item.categoryId,
        categoryName: category?.name || null,
        displayOrder: item.displayOrder || 0,
      });
    };

    rawCategories.forEach((category) => {
      const transformedCategory = {
        id: category.id,
        name: category.name,
        description: category.description || null,
        icon: this.getCategoryIcon(category.name),
        image: category.imageUrl || null,
        isAvailable: category.isAvailable !== false,
        displayOrder: category.displayOrder || 0,
      };

      categories.push(transformedCategory);

      const categoryItems = (category.items || []).map((item) => {
        const priceAmount =
          typeof item.price?.amount === 'number'
            ? item.price.amount
            : typeof item.price === 'number'
              ? item.price
              : Number(item.price?.amount ?? 0);

        const currencyCode = item.price?.currency || item.currency || menuData?.currency || 'PLN';
        const isPromoted = Boolean(
          item.isPromotedToday ??
          item.metadata?.isPromotedToday ??
          item.metadata?.promoted ??
          item.metadata?.highlighted,
        );

        const transformedItem = {
          id: item.id,
          name: item.name,
          description: item.description || '',
          shortDescription: item.shortDescription || '',
          price: priceAmount || 0,
          currency: currencyCode,
          currencySymbol: this.getCurrencySymbol(currencyCode),
          image: item.image || item.imageUrl || null,
          isAvailable: item.isAvailable !== false,
          isImageGenerated: item.isImageGenerated || false,
          status: item.status || 'available',
          categoryId: item.categoryId || category.id,
          sku: item.sku || null,
          modifiers: item.modifiers || [],
          dietary: this.extractDietaryInfo(item),
          badge: this.getItemBadge(item),
          displayOrder: item.displayOrder || 0,
          metadata: item.metadata || {},
          isPromotedToday: isPromoted,
        };

        if (isPromoted) {
          pushSpecial(transformedItem, transformedCategory);
        }

        return transformedItem;
      });

      categoryItems.sort((a, b) => a.displayOrder - b.displayOrder);

      items[category.id] = categoryItems;
    });

    categories.sort((a, b) => a.displayOrder - b.displayOrder);

    const uniqueSpecials = specials
      .filter((special, index, arr) => arr.findIndex((s) => s.id === special.id) === index)
      .sort((a, b) => {
        if (a.displayOrder === b.displayOrder) {
          return a.name.localeCompare(b.name);
        }
        return a.displayOrder - b.displayOrder;
      })
      .slice(0, 6);

    return {
      categories: categories,
      items: items,
      specials: uniqueSpecials,
    };
  }

  getCurrencySymbol(currencyCode) {
    if (!currencyCode) {
      return null;
    }

    const upper = currencyCode.toUpperCase();

    const symbolMap = {
      PLN: 'zł',
      USD: '$',
      EUR: '€',
      GBP: '£',
      CHF: 'CHF',
    };

    if (symbolMap[upper]) {
      return symbolMap[upper];
    }

    try {
      const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: upper,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      const formatted = formatter.format(0);
      const symbol = formatted.replace(/[0-9.,\s]/g, '');
      return symbol || upper;
    } catch (error) {
      console.warn('[RestaurantAPI] Unable to resolve currency symbol for', currencyCode, error);
      return upper;
    }
  }

  /**
   * Extract restaurant name from API response
   * @param {Object} apiData - API response data
   * @returns {string|null} Restaurant name
   */
  extractRestaurantName(apiData) {
    return apiData?.info?.title ||
           apiData?.restaurant?.name ||
           apiData?.config?.restaurant?.name ||
           null;
  }

  /**
   * Extract tagline from API response
   * @param {Object} apiData - API response data
   * @returns {string|null} Restaurant tagline
   */
  extractTagline(apiData) {
    return apiData?.info?.description ||
           apiData?.restaurant?.tagline ||
           apiData?.config?.restaurant?.tagline ||
           null;
  }

  /**
   * Extract color values from API response
   * @param {Object} apiData - API response data
   * @param {string} colorType - Type of color (primary, secondary, accent, etc.)
   * @returns {string|null} Color value
   */
  extractColor(apiData, colorType) {
    const colorPalette = apiData?.kioskDisplaySettings?.colorPalette;
    if (!colorPalette) return null;

    return colorPalette[colorType] || null;
  }

  /**
   * Extract logo URL from API response
   * @param {Object} apiData - API response data
   * @returns {string|null} Logo URL
   */
  extractLogo(apiData) {
    const possibleLogoPaths = [
      apiData?.kioskDisplaySettings?.welcomeScreen?.logoSettings?.url,
      apiData?.branding?.logo,
      apiData?.config?.branding?.logo,
      apiData?.kioskDisplaySettings?.branding?.logo,
      apiData?.customization?.logo,
      apiData?.logo,
      apiData?.logoUrl,
      apiData?.svgTheming?.logo,
      apiData?.customization?.branding?.logo
    ];

    const logoUrl = possibleLogoPaths.find(path => path && path.length > 0);

    if (logoUrl) {
      console.log('Logo found in backend configuration:', logoUrl);
      const logoSettings = apiData?.kioskDisplaySettings?.welcomeScreen?.logoSettings;
      if (logoSettings) {
        console.log('Logo settings:', {
          alt: logoSettings.alt,
          width: logoSettings.width,
          height: logoSettings.height
        });
      }

      if (logoUrl.startsWith('/')) {
        return `${this.baseURL}${logoUrl}`;
      }
      return logoUrl;
    }

    console.log('No logo found in backend configuration');
    return null;
  }

  /**
   * Extract background image URL from API response
   * @param {Object} apiData - API response data
   * @returns {string|null} Background image URL
   */
  extractBackgroundImage(apiData) {
    const possiblePaths = [
      apiData?.kioskDisplaySettings?.welcomeScreen?.backgroundImageUrl,
      apiData?.branding?.backgroundImage,
      apiData?.config?.branding?.backgroundImage,
      apiData?.kioskDisplaySettings?.branding?.backgroundImage,
      apiData?.customization?.backgroundImage,
      apiData?.backgroundImage,
      apiData?.backgroundImageUrl
    ];

    const bgUrl = possiblePaths.find(path => path && path.length > 0);

    if (bgUrl) {
      console.log('Background image found in backend configuration:', bgUrl);

      const bgOpacity = apiData?.kioskDisplaySettings?.welcomeScreen?.backgroundImageOpacity;
      if (bgOpacity !== undefined) {
        console.log('Background image opacity:', bgOpacity);
      }

      if (bgUrl.startsWith('/')) {
        return `${this.baseURL}${bgUrl}`;
      }
      return bgUrl;
    }

    return null;
  }

  /**
   * Extract menu categories from API response
   * @param {Object} apiData - API response data
   * @returns {Array} Menu categories
   */
  extractCategories(apiData) {

    return apiData?.config?.menu?.categories ||
           apiData?.menu?.categories ||
           apiData?.categories ||
           [];
  }

  /**
   * Extract menu items from API response
   * @param {Object} apiData - API response data
   * @returns {Object} Menu items organized by category
   */
  extractMenuItems(apiData) {

    return apiData?.config?.menu?.items ||
           apiData?.menu?.items ||
           apiData?.menuItems ||
           apiData?.items ||
           {};
  }

  /**
   * Extract special dishes from API response
   * @param {Object} apiData - API response data
   * @returns {Array} Special dishes
   */
  extractSpecials(apiData) {
    return apiData?.config?.menu?.specials ||
           apiData?.menu?.specials ||
           [];
  }

  /**
   * Extract contact information from API response
   * @param {Object} apiData - API response data
   * @param {string} contactType - Type of contact info
   * @returns {string|null} Contact information
   */
  extractContact(apiData, contactType) {
    return apiData?.config?.contact?.[contactType] ||
           apiData?.contact?.[contactType] ||
           null;
  }

  /**
   * Extract settings from API response
   * @param {Object} apiData - API response data
   * @param {string} settingKey - Setting key
   * @returns {string|null} Setting value
   */
  extractSetting(apiData, settingKey) {
    return apiData?.config?.settings?.[settingKey] ||
           apiData?.settings?.[settingKey] ||
           null;
  }


  /**
   * Fetch client's restaurants from API
   * @returns {Promise<Array>} List of restaurant options
   */
  async getClientRestaurants() {
    try {
      const response = await fetch(`${this.baseURL}/api/v1/restaurants/my-client`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'X-Restaurant-Id': 'owner',
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch restaurants: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return this.transformRestaurantsResponse(data);
    } catch (error) {
      console.error('Failed to fetch client restaurants:', error);
      throw error;
    }
  }

  /**
   * Transform restaurants API response to our format
   * @param {Object} apiData - Raw API response
   * @returns {Array} Transformed restaurants list
   */
  transformRestaurantsResponse(apiData) {
    if (!apiData || !apiData.items || !Array.isArray(apiData.items)) {
      throw new Error('Invalid API response: missing or malformed items data');
    }

    return apiData.items.map(restaurant => ({
      id: restaurant.id,
      name: restaurant.name,
      isActive: restaurant.isActive,
      city: restaurant.city,
      subdomain: restaurant.subdomain
    }));
  }


  /**
   * Get category icon based on category name
   * @param {string} categoryName - Category name
   * @returns {string} Font Awesome icon class
   */
  getCategoryIcon(categoryName) {
    const name = categoryName.toLowerCase();
    if (name.includes('dania')) return 'fas fa-utensils';
    if (name.includes('wegetariańskie')) return 'fas fa-leaf';
    if (name.includes('kubełki')) return 'fas fa-ice-cream';
    if (name.includes('kapsalon')) return 'fas fa-hamburger';
    if (name.includes('zestawy')) return 'fas fa-boxes';
    if (name.includes('mięso')) return 'fas fa-drumstick-bite';
    if (name.includes('zapiekanki')) return 'fas fa-pizza-slice';
    if (name.includes('frytki')) return 'fas fa-french-fries';
    if (name.includes('sosy')) return 'fas fa-bottle-droplet';
    if (name.includes('napoje')) return 'fas fa-glass-water';
    if (name.includes('kanapki')) return 'fas fa-bread-slice';
    return 'fas fa-utensils';
  }

  /**
   * Extract dietary information from item
   * @param {Object} item - Menu item
   * @returns {Array} Dietary restrictions
   */
  extractDietaryInfo(item) {
    const dietary = [];
    const name = item.name?.toLowerCase() || '';
    const desc = item.description?.toLowerCase() || '';

    if (name.includes('wegetariańsk') || desc.includes('wegetariańsk')) {
      dietary.push('vegetarian');
    }
    if (name.includes('wegańsk') || desc.includes('wegańsk')) {
      dietary.push('vegan');
    }
    if (name.includes('bezgluten') || desc.includes('bezgluten')) {
      dietary.push('gluten-free');
    }
    if (name.includes('ostr') || desc.includes('ostr') || desc.includes('chili')) {
      dietary.push('spicy');
    }

    return dietary;
  }

  /**
   * Get item badge based on item properties
   * @param {Object} item - Menu item
   * @returns {string|null} Badge type
   */
  getItemBadge(item) {
    if (item.isPromotedToday || item.metadata?.isPromotedToday) return 'featured';
    if (item.status === 'new') return 'new';
    if (item.status === 'popular') return 'popular';
    if (item.status === 'recommended') return 'recommended';
    return null;
  }

  /**
   * Check if running in development mode
   * @returns {boolean} Is development mode
   */
  isDevEnvironment() {
    return this.isDevMode;
  }

  /**
   * Get translation status and enabled languages
   * @param {string} restaurantId - Restaurant ID
   * @returns {Promise<Object>} Translation status with enabled languages
   */
  async getTranslationStatus(restaurantId) {
    try {
      const url = `${this.baseURL}/api/v1/device/restaurants/configuration?advanced=false`;
      console.log('[RestaurantAPI] Fetching translation status from:', url);
      console.log('[RestaurantAPI] Auth token present:', !!this.authToken);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(this.authToken ? { 'Authorization': `Bearer ${this.authToken}` } : {})
        }
      });

      console.log('[RestaurantAPI] Response status:', response.status);
      console.log('[RestaurantAPI] Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[RestaurantAPI] Error response body:', errorText);
        throw new Error(`Failed to fetch translation status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[RestaurantAPI] Translation status data:', data);

      const languageConfig = data.languageConfiguration;
      if (languageConfig) {
        const translationStatus = {
          enabledLanguages: languageConfig.enabled || ['en'],
          sourceLanguage: languageConfig.sourceLanguage || 'en',
          defaultLanguage: languageConfig.defaultLanguage || 'en',
          fallbackLanguage: languageConfig.fallbackLanguage || 'en'
        };
        console.log('[RestaurantAPI] Extracted language config:', translationStatus);
        return translationStatus;
      } else {
        throw new Error('No language configuration found in response');
      }
    } catch (error) {
      console.error('[RestaurantAPI] Error fetching translation status:', error);

      if (restaurantId && restaurantId.toLowerCase().includes('kery')) {
        const keryFallback = {
          enabledLanguages: ['pl', 'en'],
          sourceLanguage: 'pl',
          defaultLanguage: 'pl'
        };
        console.log('[RestaurantAPI] Returning Kery-specific fallback:', keryFallback);
        return keryFallback;
      }

      const fallback = {
        enabledLanguages: ['en'],
        sourceLanguage: 'en',
        defaultLanguage: 'en'
      };
      console.log('[RestaurantAPI] Returning default fallback:', fallback);
      return fallback;
    }
  }
}
